import {
  CONVERSATION_MODE_LABELS,
  OPPORTUNITY_CONTROL_LABELS,
  type ConversationMode,
  type OpportunityControlState,
} from '@lia/contracts';
import { HandIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatRelative } from '../../lib/format.js';
import { Avatar } from '../primitives/avatar.js';
import { StatusBadge } from '../primitives/badge.js';
import { CONVERSATION_MODE_TONE, OPPORTUNITY_CONTROL_TONE } from './status.js';

export interface ConversationItemProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  name: string;
  preview: string;
  /** ISO time of the last message. */
  lastMessageAt: string;
  unreadCount?: number;
  mode: ConversationMode;
  /** A person must respond (handoff requested). */
  humanNeeded?: boolean;
  /**
   * Handoff state of the conversation's opportunity. «Transferencia pendiente» / «Atendido por humano — definitivo»
   * replace the mode badge (LIA never resumes a final one on its own).
   */
  aiControl?: OpportunityControlState;
  /** e.g. "Torre Alameda" */
  projectName?: string;
  selected?: boolean;
  now?: Date;
}

/** Inbox list row. Render inside a <ul role="list"> / <li>. */
export const ConversationItem = React.forwardRef<
  HTMLButtonElement,
  ConversationItemProps
>(function ConversationItem(
  {
    name,
    preview,
    lastMessageAt,
    unreadCount = 0,
    mode,
    humanNeeded,
    aiControl = 'ai_active',
    projectName,
    selected,
    now,
    className,
    ...props
  },
  ref,
) {
  const unread = unreadCount > 0;
  return (
    <button
      ref={ref}
      type="button"
      aria-current={selected || undefined}
      className={cn(
        'flex w-full items-start gap-3 rounded-control px-3 py-3 text-left transition-colors duration-[var(--ef-motion-fast)]',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus',
        selected ? 'bg-accent-selected' : 'hover:bg-surface-subtle',
        className,
      )}
      {...props}
    >
      <Avatar name={name} size="md" />
      <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)] gap-1">
        <span className="flex items-baseline gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-body text-text-primary',
              unread ? 'font-semibold' : 'font-medium',
            )}
          >
            {name}
          </span>
          <span className="ef-tabular shrink-0 text-meta text-text-secondary">
            {formatRelative(lastMessageAt, now)}
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-body',
              unread ? 'text-text-primary' : 'text-text-secondary',
            )}
          >
            {preview}
          </span>
          {unread ? (
            <span className="ef-tabular inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-pill bg-brand-primary px-1.5 text-meta font-semibold text-text-on-primary">
              {unreadCount}
              <span className="sr-only"> sin leer</span>
            </span>
          ) : null}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          {humanNeeded && aiControl !== 'human_final' ? (
            <StatusBadge tone="danger" icon={<Icon icon={HandIcon} aria-hidden />}>
              Requiere asesor
            </StatusBadge>
          ) : null}
          {aiControl === 'ai_active' ? (
            <StatusBadge tone={CONVERSATION_MODE_TONE[mode]}>
              {CONVERSATION_MODE_LABELS[mode]}
            </StatusBadge>
          ) : (
            <StatusBadge tone={OPPORTUNITY_CONTROL_TONE[aiControl]}>
              {OPPORTUNITY_CONTROL_LABELS[aiControl]}
            </StatusBadge>
          )}
          {projectName ? (
            <span className="min-w-0 max-w-full truncate text-meta text-text-secondary">
              {projectName}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
});
